import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Logo } from "@/components/Logo";
import { Button, Field, TextInput } from "@/components/primitives";
import { useAuth } from "@/lib/auth";
import { createYard } from "@/lib/yard-data";

export const Route = createFileRoute("/onboarding")({
  head: () => ({
    meta: [
      { title: "Set up your yard — PortDwellr" },
      { name: "description", content: "Name your yard, set the timezone and the detention-free time threshold before the loop starts." },
      { property: "og:title", content: "Set up your yard — PortDwellr" },
      { property: "og:description", content: "One step before the gate manifest goes live." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: OnboardingPage,
});

const TZ = [
  "America/Los_Angeles",
  "America/Denver",
  "America/Chicago",
  "America/New_York",
  "America/Vancouver",
  "Europe/Rotterdam",
];

function OnboardingPage() {
  const { session, ready, completeYardSetup } = useAuth();
  const navigate = useNavigate();
  const [yardName, setYardName] = useState("");
  const [address, setAddress] = useState("");
  const [timezone, setTimezone] = useState(TZ[0]!);
  const [freeTime, setFreeTime] = useState("2");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!ready) return;
    if (!session) navigate({ to: "/auth", replace: true });
    else if (session.yardConfigured) navigate({ to: "/", replace: true });
  }, [ready, session, navigate]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const next: Record<string, string> = {};
    if (!yardName.trim()) next["yardName"] = "Enter a yard name";
    if (!address.trim()) next["address"] = "Enter the yard address";
    const hours = Number(freeTime);
    if (!freeTime.trim() || Number.isNaN(hours) || hours < 1 || hours > 24) {
      next["freeTime"] = "Enter free time between 1 and 24 hours";
    }
    setErrors(next);
    if (Object.keys(next).length > 0) {
      const first = document.querySelector<HTMLElement>("[data-error='true']");
      first?.focus();
      return;
    }

    setSaving(true);
    try {
      // Real insert first — nothing navigates until the yard row exists.
      await createYard({
        yardName: yardName.trim(),
        address: address.trim(),
        timezone,
        freeTimeHours: hours,
      });
      await completeYardSetup();
      const pending = sessionStorage.getItem("pd_tier");
      if (pending === "gate" || pending === "yard") {
        sessionStorage.removeItem("pd_tier");
        navigate({ to: "/settings/billing", search: { tier: pending }, replace: true });
      } else {
        navigate({ to: "/", replace: true });
      }
    } catch (err) {
      setErrors({ form: err instanceof Error ? err.message : "Could not create the yard. Try again." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-paper">
      <header className="border-b border-line">
        <div className="mx-auto flex max-w-[1180px] items-center justify-between px-5 py-[14px] md:px-8">
          <Logo />
          <span className="font-mono text-[11px] uppercase tracking-[0.08em] text-steel-mid">Step 1 of 1</span>
        </div>
      </header>

      <main className="mx-auto max-w-[760px] px-5 py-14 md:px-8">
        <span className="eyebrow">Before SENSE can start</span>
        <h1 className="mt-4 font-display text-[32px] font-semibold leading-[1.1] md:text-[38px]">Set up your yard</h1>
        <p className="mt-3 max-w-[58ch] text-[15px] text-ink-soft">
          The loop needs a yard to run on. Tell us where the gate is and how much free time carriers get before the
          detention clock starts — every screen after this reads from these values.
        </p>

        <form onSubmit={submit} noValidate className="mt-10 border border-line bg-paper-raised">
          <div className="grid grid-cols-1 gap-6 p-6 md:grid-cols-2 md:p-8">
            <div className="md:col-span-2">
              <Field label="Yard name" error={errors["yardName"]}>
                <TextInput
                  value={yardName}
                  data-error={errors["yardName"] ? "true" : undefined}
                  aria-invalid={errors["yardName"] ? true : undefined}
                  onChange={(e) => {
                    setYardName(e.target.value);
                    setErrors(({ yardName: _drop, ...rest }) => rest);
                  }}
                  placeholder="e.g. Port Terminal Yard 4"
                />
              </Field>
            </div>
            <div className="md:col-span-2">
              <Field label="Yard address" error={errors["address"]}>
                <TextInput
                  value={address}
                  data-error={errors["address"] ? "true" : undefined}
                  aria-invalid={errors["address"] ? true : undefined}
                  onChange={(e) => {
                    setAddress(e.target.value);
                    setErrors(({ address: _drop, ...rest }) => rest);
                  }}
                  placeholder="e.g. 1180 Harbor Access Rd, Long Beach, CA"
                />
              </Field>
            </div>
            <Field label="Timezone">
              <select
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                className="w-full border border-line-strong bg-paper px-3 py-[10px] font-mono text-[14px] text-ink outline-none focus:ring-2 focus:ring-steel/30"
              >
                {TZ.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </Field>
            <Field
              label="Detention-free time (hours)"
              hint="Wait time past this threshold counts as detention exposure."
              error={errors["freeTime"]}
            >
              <TextInput
                type="number"
                min={1}
                max={24}
                step={0.5}
                value={freeTime}
                data-error={errors["freeTime"] ? "true" : undefined}
                aria-invalid={errors["freeTime"] ? true : undefined}
                onChange={(e) => {
                  setFreeTime(e.target.value);
                  setErrors(({ freeTime: _drop, ...rest }) => rest);
                }}
              />
            </Field>
          </div>
          {errors["form"] ? (
            <p role="alert" className="border-t border-line px-6 pt-4 font-mono text-[12px] text-destructive md:px-8">
              {errors["form"]}
            </p>
          ) : null}
          <div className="flex items-center justify-between gap-4 border-t border-line px-6 py-5 md:px-8">
            <span className="font-mono text-[11px] uppercase tracking-[0.08em] text-steel-mid">
              You can change all of this in Settings
            </span>
            <Button type="submit" disabled={saving}>
              {saving ? "Creating yard…" : "Start the loop"}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}
